import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { SpotSpotMatrix } from '@approot/models/spot-spot-matrix';
import { PricingData } from '@approot/models/strategy-payload';
import {
  AlertsService,
  LegService,
  PricingExtraService
} from '@approot/services';
import {
  SubscriptionSink,
  Utilities,
  exponentialDelay,
  shortUuid,
  sleep
} from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import copy from 'copy-to-clipboard';
import { fromEvent } from 'rxjs';

@Component({
  selector: 'app-spot-spot-matrix',
  templateUrl: './spot-spot-matrix.component.html',
  styleUrls: ['./spot-spot-matrix.component.scss']
})
export class SpotSpotMatrixComponent implements OnInit, OnDestroy {
  @Output() spotSpotMatrixOpenChanged = new EventEmitter();
  @Input() isModalOpened = false;
  @Input() legComponents: LegComponent[] = [];
  reportingCurrency: any[] = [];
  activeCurrencyPairs: string[] = [];
  firstCrossOptions: string[] = [];
  secondCrossOptions: string[] = [];
  form: FormGroup;
  page = 0;
  maxValue = 0;
  minValue = 0;
  reportingCurrencyUsed = '';
  areLegsValid = true;
  calculatingStatus = {
    status: '',
    message: ''
  };

  pricingData: PricingData = {};

  matrixResponse: SpotSpotMatrix;
  taskSent;
  private webSocketUUID = 'spot-spot-matrix_' + shortUuid();
  private subSink = new SubscriptionSink();
  constructor(
    private legService: LegService,
    private elementRef: ElementRef,
    private pricingExtraService: PricingExtraService,
    private webSocketService: GlobalWebsocketService,
    private alertService: AlertsService
  ) {}
  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  setMinMaxValues() {
    // find the max and min values from the data returned by the server value field.
    let minValue = null;
    let maxValue = null;

    this.matrixResponse.data.forEach(arrayValues => {
      const minOfArray = Math.min(...arrayValues.values);
      const maxOfArray = Math.max(...arrayValues.values);

      minValue = // store the min value
        minValue === null || minOfArray < minValue ? minOfArray : minValue;

      maxValue = // store max value
        maxValue === null || maxOfArray > maxValue ? maxOfArray : maxValue;
    });

    this.minValue = minValue;
    this.maxValue = maxValue;
  }

  modalChanged(event) {
    this.spotSpotMatrixOpenChanged.emit(event);
  }

  ngOnInit() {
    this.listenToWebSocket();
    this.form = new FormGroup({
      first: new FormGroup({
        cross: new FormControl('', Validators.required),
        minSpot: new FormControl('', Validators.required),
        maxSpot: new FormControl('', Validators.required),
        numberOfPoints: new FormControl('', Validators.required)
      }),
      second: new FormGroup({
        cross: new FormControl('', Validators.required),
        minSpot: new FormControl('', Validators.required),
        maxSpot: new FormControl('', Validators.required),
        numberOfPoints: new FormControl('', Validators.required)
      }),
      reportingCurrency: new FormControl('', Validators.required)
    });

    // we need to remove the currency that is selected in one dropDown, from a possible option in another

    this.form
      .get('first')
      .get('cross')
      .valueChanges.subscribe(x => {
        this.secondCrossOptions = this.filterCross(x);
      });

    this.form
      .get('second')
      .get('cross')
      .valueChanges.subscribe(x => {
        this.firstCrossOptions = this.filterCross(x);
      });

    this.legComponents.map(x => {
      if (!x.form.valid) {
        // if a leg is not valid then all legs are not valid
        this.areLegsValid = false;
      }

      if (
        x.form.controls['cross'] &&
        x.form.controls['cross'].value &&
        this.activeCurrencyPairs.indexOf(x.form.controls['cross'].value) === -1
      ) {
        this.activeCurrencyPairs.push(x.form.controls['cross'].value);
      }

      if (
        // if the component has 2 currency pairs e.g. chooser
        x.form.controls['secondCross'] &&
        x.form.controls['secondCross'].value &&
        this.activeCurrencyPairs.indexOf(
          x.form.controls['secondCross'].value
        ) === -1
      ) {
        this.activeCurrencyPairs.push(x.form.controls['secondCross'].value);
      }
    });
    this.firstCrossOptions = this.activeCurrencyPairs;
    this.secondCrossOptions = this.activeCurrencyPairs;

    this.subSink.sink = fromEvent(document, 'keydown').subscribe(event => {
      // we want to listen to the f9 button press
      this.keypress(event);
    });
  }

  keypress(event) {
    if (
      event.keyCode === 120 &&
      this.calculatingStatus.status !== 'pending' &&
      this.calculatingStatus.status !== 'webSocketDown'
    ) {
      // if the key is f9 and the forms are valid, calculate the spot
      if (this.form.valid) {
        // if the form is valid then calculate the spot
        this.submit();
      }
    }
  }

  filterCross(cross) {
    this.setReportingCurrency(); // when its going to filter the cross, generate new options for the report currency
    return this.activeCurrencyPairs.filter(x => x !== cross);
  }

  setReportingCurrency() {
    // when a currency pair changes the reporting currency options should change
    let reportingCurrency = [];
    const cross1 = this.form.get('first').get('cross').value;
    const cross2 = this.form.get('second').get('cross').value;
    if (cross1) {
      reportingCurrency.push(cross1.substring(0, 3));
      reportingCurrency.push(cross1.substring(3, 6));
    }

    // We cant have duplicates

    if (cross2) {
      if (reportingCurrency.indexOf(cross2.substring(0, 3)) === -1) {
        reportingCurrency.push(cross2.substring(0, 3));
      }

      if (reportingCurrency.indexOf(cross2.substring(3, 6)) === -1) {
        reportingCurrency.push(cross2.substring(3, 6));
      }
    }

    // if the reporting currency has been set and its in the options after the change just leave it else wipe it

    if (
      reportingCurrency.indexOf(this.form.get('reportingCurrency').value) === -1
    ) {
      this.form.get('reportingCurrency').setValue('');
    }

    this.reportingCurrency = reportingCurrency;
  }

  submit() {
    this.calculatingStatus.status = 'pending';

    //join all the market data from all the legs
    this.pricingData = {
      fixings: [],
      treasuryRates: [],
      marketData: []
    };
    let filteredRequiredFixings = [];

    this.legComponents.forEach((legComponent: LegComponent) => {
      if (legComponent.legHelperService.requiredFixings) {
        let fixings = legComponent.legPricingService.getFixings();
        filteredRequiredFixings = fixings.concat(fixings);
      }
    });

    this.pricingData.fixings = filteredRequiredFixings;

    this.price();
  }

  price() {
    this.taskSent = {
      taskType: 'spotSpotMatrix',
      taskParameters: {
        reportingCurrency: this.form.get('reportingCurrency').value,
        cross1: this.getCross1(),
        minSpot1: '' + this.form.get('first').get('minSpot').value,
        maxSpot1: '' + this.form.get('first').get('maxSpot').value,
        numpts1: '' + this.form.get('first').get('numberOfPoints').value,
        cross2: this.getCross2(),
        minSpot2: '' + this.form.get('second').get('minSpot').value,
        maxSpot2: '' + this.form.get('second').get('maxSpot').value,
        numpts2: '' + this.form.get('second').get('numberOfPoints').value,
        output: ['Price']
      }
    };
    let payload = this.legService.createServicePayload(
      this.pricingExtraService.getMarketDataTimeStamp,
      this.pricingExtraService.marketDataSpreadingRule,
      this.legComponents.map(y => {
        return { product: y.form.value, attributes: y.leg.attributes };
      }),
      this.taskSent,
      this.pricingData
    );

    this.webSocketService.sendMessage({
      action: `${API_PREFIX_V2}pricing`,
      payload,
      uuid: this.webSocketUUID
    });
  }

  private listenToWebSocket(): void {
    const resubscribe = (iteration: number) => {
      this.calculatingStatus.status = '';
      if (iteration > 5) {
        this.alertService.addAlert({
          message:
            'There was a problem setting up the WebSocket for the spot spot matrix',
          type: 'error'
        });
        return;
      }
      this.subSink.sink = this.webSocketService
        .getListener({
          action: `${API_PREFIX_V2}pricing`,
          uuid: this.webSocketUUID,
          take: Infinity
        })
        .subscribe({
          next: resp => {
            this.calculatingStatus.status = '';
            if (resp.error) {
              this.alertService.addAlert({
                message: resp.error.message,
                type: 'error'
              });
            } else {
              this.handleMatrixResponse(resp.payload as SpotSpotMatrix);
            }
          },
          complete: async () => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          error: async _ => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleMatrixResponse(payload: SpotSpotMatrix): void {
    if (payload) {
      this.reportingCurrencyUsed =
        this.taskSent.taskParameters.reportingCurrency;
      this.matrixResponse = payload;
      this.setMinMaxValues();
      this.page = 1;
    }
  }

  getCross1(): string {
    return (
      this.form.get('first').get('cross').value.substring(0, 3) +
      '/' +
      this.form.get('first').get('cross').value.substring(3, 6)
    );
  }

  getCross2(): string {
    return (
      this.form.get('second').get('cross').value.substring(0, 3) +
      '/' +
      this.form.get('second').get('cross').value.substring(3, 6)
    );
  }

  formatTableData() {
    // this function is used to convert the table into the excel format, and then return it for copying
    const table = this.elementRef.nativeElement.querySelector('#table-data');

    let results = '';
    if (this.matrixResponse) {
      results =
        ' ' +
        '\t' +
        this.matrixResponse.columnTitle +
        '\n' +
        this.matrixResponse.lineTitle +
        Utilities.tableToExcelFormat(table);
    }

    // we need to add the top and left columns to the excel format
    return results;
  }

  onCopyToClipboardClicked() {
    copy(this.formatTableData(), { format: 'text/plain' }); // copy the text. keep in text/plain, so that tabs and new lines stay
  }
}
